import type {
  NormalizedPin,
  Panorama,
  Space,
  StandardCoordProps,
  Walkway,
} from '@/modules/defines/interface';
import arrowLeft from '@/assets/icon/arrow-left.svg';
import map_current_pin from '@/assets/icon/map-pin-selected.svg';
import close from '@/assets/icon/circle-x-filled.svg';
import caret from '@/assets/icon/caret-up-down-filled.svg';
import map_start_pin from '@/assets/icon/map-pin-start.svg';
import map_end_pin from '@/assets/icon/map-pin-end.svg';
import {
  mapOptions,
  spaceCoords1FAtti,
  spaceCoords1FRaon,
  spaceCoords2FAtti,
  spaceCoords2FRaon,
  spaceCoordsB1FRaon,
  parkingCoordB2F,
  parkingCoordB3F,
} from '@/modules/defines/datas';
import { body1, body2, body3, h3, palette } from '@/modules/defines/styles';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { useGesture } from '@use-gesture/react';
import getSessionPoint from '@/utils/function/getSessionPoint';
import setPoint from '@/utils/function/setPoint';
import updateZoneState from '@/utils/function/updateZoneState';
import getZoneMap from '@/utils/function/getZoneMap';
import BottomSheet from '@/components/modal/BottomSheet';
import FloatingButton from '@/components/navi/FloatingButton';
import SpaceFilterList from '@/components/main/SpaceFilterList';
import SpaceModal from '@/components/modal/SpaceModal';
import TransitPin from '@/assets/icon/minimap/TransitPin';
import { pagePaths } from '@/modules/defines/paths';
import useNavigationStore from '@/store/navigation';
import { dijkstra } from '@/utils/function/dijkstra';
import Chip from '@/components/navi/Chip';
import ToolTipList from '@/components/navi/TooltipList';
import { Globals } from '@react-spring/three';
import LoadingModal from '@/components/modal/LoadingModal';
import Search from '@/components/navi/Search';
import { logActivateNavi, logPageView } from '@/utils/amplitude';
type PathType = 'Stair' | 'Escalator' | 'Elevator' | 'Short';

interface NaviProps {
  spaceData: Space[] | undefined;
  walkData: Walkway[] | undefined;
  isLoading: boolean;
}

const ZOOM_LEVEL = 0.25;
const INITIAL_X = -165;
const INITIAL_Y = -300;
const ZOOM_SENSITIVITY_MOBILE = 0.2;
const ZOOM_SENSITIVITY_DESKTOP = 0.2;
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 3;
const IMAGE_WIDTH = 1800;
const IMAGE_HEIGHT = 1400;

const NaviContainer = ({ spaceData, walkData, isLoading }: NaviProps) => {
  const graph = useNavigationStore((state) => state.graph);
  const storedStart = sessionStorage.getItem('start');
  const storedEnd = sessionStorage.getItem('end');
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSpaceModalOpen, setIsSpaceModalOpen] = useState(false);
  const [openList, setOpenList] = useState<'start' | 'end' | null>(null);

  const [value, setValue] = useState({
    start: storedStart ? JSON.parse(storedStart).title : '',
    end: storedEnd ? JSON.parse(storedEnd).title : '',
  });

  const [selectedStart, setSelectedStart] = useState<Space | null>(null);
  const [selectedEnd, setSelectedEnd] = useState<Space | null>(null);

  const [startPoint, setStartPoint] = useState<NormalizedPin | null>(null);
  const [endPoint, setEndPoint] = useState<NormalizedPin | null>(null);
  // const [pins, setPins] = useState<Pin[]>([]);
  const [zoneState, setZoneState] = useState<string>('1F_raon'); // B3F, B2F, B1F, 1F_atti, 1F_raon, 2F_atti, 2F_raon
  const [currentPin, setCurrentPin] = useState<NormalizedPin | null>(null); // 핀 찍은 공간 위치
  const [standardCoordData, setStandardCoordData] = useState<
    StandardCoordProps[]
  >([]);

  const [selectedPath, setSelectedPath] = useState<PathType>('Short');
  const [matchedFloor, setMatchedFloor] = useState<string[]>([]);
  const [result, setResult] = useState<any | null>(null);
  const [validated, setValidated] = useState(false);

  const query = new URLSearchParams(window.location.search);
  const queryStart = query.get('start');

  // const { openModal: openImgModal, closeModal: closeImgModal } = useModal();
  const [{ x, y, scale }, api] = useSpring(() => ({
    x: INITIAL_X,
    y: INITIAL_Y,
    scale: ZOOM_LEVEL,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
    },
  }));
  const boundsRef = useRef({ left: -100, right: 100, top: -100, bottom: 50 });

  useEffect(() => {
    console.log('jenkins test');
    document.body.style.pointerEvents = 'auto';
    api.start({
      x: INITIAL_X,
      y: INITIAL_Y,
      scale: ZOOM_LEVEL,
    });

    return () => {
      api.stop();
    };
  }, []);

  useEffect(() => {
    const initialStart = storedStart ? JSON.parse(storedStart) : null;
    const initialEnd = storedEnd ? JSON.parse(storedEnd) : null;
    if (initialStart && spaceData) {
      if (initialStart.isParked) {
        sessionStorage.removeItem('start');
        updateZoneState({
          floor: initialStart.floor,
          setZoneState,
          setStandardCoordData,
        });
        setSelectedStart({
          id: 0,
          type: '',
          floor: initialStart.floor,
          title: initialStart.title,
          zone: initialStart.spot,
          default_pano: initialStart.default_pano,
        } as Space);
      } else {
        sessionStorage.removeItem('start');
        const space = spaceData.find((space) => space.id === initialStart.id);
        if (space) {
          updateZoneState({
            space,
            setZoneState,
            setStandardCoordData,
          });
          setSelectedStart(space);
        }
      }
    } else if (initialEnd && spaceData) {
      if (initialEnd.isParked) {
        sessionStorage.removeItem('end');
        updateZoneState({
          floor: initialEnd.floor,
          setZoneState,
          setStandardCoordData,
        });
        setSelectedEnd({
          id: 0,
          type: '',
          floor: initialEnd.floor,
          title: initialEnd.title,
          zone: initialEnd.spot,
          default_pano: initialEnd.default_pano,
        } as Space);
      } else {
        sessionStorage.removeItem('end');
        const space = spaceData.find((space) => space.id === initialEnd.id);
        if (space) {
          updateZoneState({
            space,
            setZoneState,
            setStandardCoordData,
          });
          setSelectedEnd(space);
        }
      }
    }
  }, [storedEnd, spaceData, storedStart]);

  // query 있을 때 (QR 코드 용 )
  useEffect(() => {
    if (queryStart && spaceData) {
      // sessionStorage.removeItem('start');
      const space = spaceData?.find(
        (space) => space.id === parseInt(queryStart)
      );
      if (space) {
        // sessionStorage.setItem('start', JSON.stringify(space));
        updateZoneState({
          space,
          setZoneState,
          setStandardCoordData,
        });
        setSelectedStart(space);
        setValue({ ...value, start: space.title });
      }
    }
  }, [spaceData, queryStart]);

  useEffect(() => {
    if (zoneState !== null) {
      getSessionPoint({
        storedPoint: storedStart,
        spaceData,
        setZoneState,
        standardCoordData,
        setStandardCoordData,
        setPoint: setStartPoint,
      });
    }
  }, [zoneState, standardCoordData]);

  useEffect(() => {
    if (!zoneState) return;

    if (selectedStart && standardCoordData) {
      setPoint({
        space: selectedStart,
        standardCoordData,
        setPoint: setStartPoint,
        zoomPoint: zoomPoint,
      });
    }

    if (selectedEnd && standardCoordData) {
      setPoint({
        space: selectedEnd,
        standardCoordData,
        setPoint: setEndPoint,
        zoomPoint: zoomPoint,
      });
    }
  }, [zoneState, selectedStart, selectedEnd, standardCoordData]);

  // 📌 존 내에 클릭 가능한 Space 좌표 설정
  useEffect(() => {
    switch (zoneState) {
      case '1F_atti':
        setStandardCoordData(spaceCoords1FAtti);
        break;
      case '1F_raon':
        setStandardCoordData(spaceCoords1FRaon);
        break;
      case '2F_atti':
        setStandardCoordData(spaceCoords2FAtti);
        break;
      case '2F_raon':
        setStandardCoordData(spaceCoords2FRaon);
        break;
      case 'B1F':
        setStandardCoordData(spaceCoordsB1FRaon);
        break;
      case 'B2F':
        setStandardCoordData(
          parkingCoordB2F.map(({ zone, coord, default_pano }) => ({
            zone,
            coord: [coord],
            default_pano,
          }))
        );
        break;
      case 'B3F':
        setStandardCoordData(
          parkingCoordB3F.map(({ zone, coord, default_pano }) => ({
            zone,
            coord: [coord],
            default_pano,
          }))
        );
        break;
      default:
        setStandardCoordData([]);
        break;
    }
  }, [zoneState]);

  useEffect(() => {
    if (startPoint && endPoint && selectedPath) {
      const temp_result = dijkstra(
        graph,
        walkData!,
        startPoint.panorama_id!,
        endPoint.panorama_id!,
        selectedPath
      );
      temp_result.panorama_path!.forEach((item, index) => {
        if (index < temp_result.path.length - 1) {
          item.connectedNode = item.connectedNode.filter(
            (node: any) => temp_result.path[index + 1] === node.id
          );
        }
      });
      const optimizedPathId: any[] = [];
      const optimizedPath: any[] = [];
      let i = 0;
      while (i < temp_result.panorama_path.length) {
        const start = temp_result.panorama_path[i];

        // 엘리베이터 경로인지 확인
        if (start.category === 'elevator') {
          const temp_upward: any[] = [];
          const temp_downward: any[] = [];
          let j = i;
          const upward_external = temp_result.panorama_path[
            i
          ].externalList.find((external: any) =>
            external.includes(temp_result.panorama_path[j].id)
          );
          if (upward_external) temp_upward.push(upward_external);
          while (
            j + 1 < temp_result.panorama_path.length &&
            temp_result.panorama_path[j + 1].category === 'elevator'
          ) {
            const downward_external = temp_result.panorama_path[
              j + 1
            ].externalList.find((external: any) =>
              external.includes(temp_result.panorama_path[j].id)
            );

            if (downward_external) {
              temp_downward.push(downward_external);
            }

            j++;
            const upward_external = temp_result.panorama_path[
              j - 1
            ].externalList.find((external: any) =>
              external.includes(temp_result.panorama_path[j].id)
            );
            if (upward_external) {
              temp_upward.push(upward_external);
            }
          }

          const end = temp_result.panorama_path[j]; // 엘리베이터 구간이 끝나는 지점

          // 연결된 노드를 조정하여 start → end 바로 연결
          if (end && start.id !== end.id) {
            start.externalList = start.externalList.map((external: any) => {
              if (external === temp_upward[0]) {
                // 🔵 기존 값 분해
                const parts = temp_upward[temp_upward.length - 1].split('_');
                const newParts = external.split('_');

                // 🟠 세 번째 값만 변경
                parts[2] = newParts[2];

                // ✅ 다시 조합하여 반환
                return parts.join('_');
              }
              return external;
            });

            end.externalList = end.externalList.map((external: any) => {
              if (external === temp_downward[temp_downward.length - 1]) {
                // 🔵 기존 값 분해
                const parts = temp_downward[0].split('_');
                const newParts = external.split('_');

                // 🟠 세 번째 값만 변경
                parts[2] = newParts[2];

                // ✅ 다시 조합하여 반환
                return parts.join('_');
              }
              return external;
            });

            start.connectedNode = start.connectedNode.filter(
              (node: any) => node.id === end.id
            );

            optimizedPath.push(start);
            optimizedPath.push(end);
            optimizedPathId.push(start.id);
            optimizedPathId.push(end.id);
          } else {
            optimizedPath.push(start);
            optimizedPathId.push(start.id);
          }

          i = j + 1;
        } else {
          optimizedPath.push(start);
          optimizedPathId.push(start.id);
          i++;
        }
      }

      const floorList = optimizedPath!.reduce(
        (acc: string[], curr: { space_id: number }) => {
          const lastSpaceId =
            acc.length > 0
              ? mapOptions.find((o) => o.floor === acc[acc.length - 1])
                  ?.walkway_id
              : null;

          if (lastSpaceId !== curr.space_id) {
            const matchedFloor = mapOptions.find(
              (option) => option.walkway_id === curr.space_id
            )?.floor;
            if (matchedFloor) acc.push(matchedFloor);
          }
          return acc;
        },
        []
      );
      temp_result.panorama_path = optimizedPath;
      temp_result.path = optimizedPathId;

      console.log('매칭된 floor 목록:', floorList);
      setMatchedFloor(floorList);
      setResult(temp_result);
    }

    if (!startPoint || !endPoint) {
      setResult(null);
      setMatchedFloor([]);
    }
  }, [startPoint, endPoint, selectedPath]);

  // 📌 드래그 및 줌 핸들러
  const [isPinch, setIsPinch] = useState(false);
  const pinchStartScaleRef = useRef(1);
  const lastEmittedScale = useRef(1); // 마지막 적용된 스케일 저장
  const SCALE_THRESHOLD = 0.07;
  const dragStartRef = useRef<[number, number] | null>(null);
  const isZoomingRef = useRef(false);
  const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useGesture(
    {
      onDrag: ({
        pinching,
        cancel,
        offset: [ox, oy],
        velocity: [vx, vy],
        direction: [dirX, dirY],
        touches,
        last,
        memo,
      }) => {
        if (!containerRef.current) return;
        if (pinching) return cancel();

        api.stop(); // 스프링 중단

        const currentScale = scale.get();
        const currentX = x.get();
        const currentY = y.get();

        // ✅ 드래그 시작 시점의 offset을 기억
        if (!memo) {
          memo = [ox, oy, currentX, currentY]; // 시작 offset, 시작 위치
        }

        const [startOx, startOy, startX, startY] = memo;
        const deltaX = (ox - startOx) * currentScale;
        const deltaY = (oy - startOy) * currentScale;

        if (touches === 1) {
          if (last) {
            const inertiaX = vx * dirX * 200;
            const inertiaY = vy * dirY * 200;

            api.start({
              x: startX + deltaX + inertiaX,
              y: startY + deltaY + inertiaY,
              config: { mass: 1, tension: 170, friction: 30 },
            });
          } else {
            api.start({
              x: startX + deltaX,
              y: startY + deltaY,
              immediate: true,
            });
          }
        }

        return memo;
      },

      onWheel: ({ event }) => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const currentScale = scale.get();
        const currentX = x.get();
        const currentY = y.get();

        const zoomFactor =
          event.deltaY > 0
            ? 1 - ZOOM_SENSITIVITY_DESKTOP
            : 1 + ZOOM_SENSITIVITY_DESKTOP;

        const newScale = Math.max(
          ZOOM_MIN,
          Math.min(ZOOM_MAX, currentScale * zoomFactor)
        );

        // ✅ 마우스 기준 확대
        const tempX = INITIAL_X + event.clientX - centerX;
        const tempY = INITIAL_Y + event.clientY - centerY;

        const biasX =
          ((tempX - currentX) * newScale) / currentScale - (tempX - currentX);
        const biasY =
          ((tempY - currentY) * newScale) / currentScale - (tempY - currentY);

        const nextX = currentX - biasX;
        const nextY = currentY - biasY;

        // 📦 bounds
        boundsRef.current = {
          left: Math.min(
            -(IMAGE_WIDTH * newScale - containerWidth) * 0.9,
            -100
          ),
          right: Math.max(
            (IMAGE_WIDTH * newScale - containerWidth) / 2 - 200,
            100
          ),
          top: Math.min(
            -(IMAGE_HEIGHT * newScale - containerHeight) * 0.9,
            -100
          ),
          bottom: Math.min(
            (IMAGE_HEIGHT * newScale - containerHeight) / 2 + 200,
            newScale > 1.7 ? 1400 : 500
          ),
        };

        // 🔒 드래그 간섭 방지
        isZoomingRef.current = true;
        if (zoomTimeoutRef.current) clearTimeout(zoomTimeoutRef.current);
        zoomTimeoutRef.current = setTimeout(() => {
          isZoomingRef.current = false;
        }, 100);

        api.start({
          scale: newScale,
          x: nextX,
          y: nextY,
          config: {
            mass: 1,
            tension: 170,
            friction: 30,
          },
        });
      },

      onPinchStart: ({ offset: [s] }) => {
        pinchStartScaleRef.current = s || 1;
        lastEmittedScale.current = s || 1;
      },
      onPinchEnd: () => {
        dragStartRef.current = null;
      },
      onPinch: ({ offset: [s], origin: [ox, oy] }) => {
        if (!containerRef.current) return;

        const isMobile = document.body.classList.contains('isMobile');
        const prevScale = lastEmittedScale.current;
        const diff = Math.abs(s - prevScale);
        if (!isMobile && diff < SCALE_THRESHOLD) return;
        lastEmittedScale.current = s;

        const newScale = Math.max(
          ZOOM_MIN,
          Math.min(ZOOM_MAX, s * ZOOM_SENSITIVITY_MOBILE)
        );

        const currentX = x.get();
        const currentY = y.get();
        const currentScale = scale.get();

        const rect = containerRef.current.getBoundingClientRect();
        const containerWidth = rect.width;
        const containerHeight = rect.height;

        const centerX = rect.left + containerWidth / 2;
        const centerY = rect.top + containerHeight / 2;

        const tempX = INITIAL_X + ox - centerX;
        const tempY = INITIAL_Y + oy - centerY;

        const biasX =
          ((tempX - currentX) * newScale) / currentScale - (tempX - currentX);
        const biasY =
          ((tempY - currentY) * newScale) / currentScale - (tempY - currentY);

        const nextX = currentX - biasX;
        const nextY = currentY - biasY;

        boundsRef.current = {
          left: Math.min(
            -(IMAGE_WIDTH * newScale - containerWidth) * 0.9,
            -100
          ),
          right: Math.max(
            (IMAGE_WIDTH * newScale - containerWidth) / 2 - 200,
            100
          ),
          top: Math.min(
            -(IMAGE_HEIGHT * newScale - containerHeight) * 0.9,
            -100
          ),
          bottom: Math.min(
            (IMAGE_HEIGHT * newScale - containerHeight) / 2 + 200,
            newScale > 1.7 ? 1400 : 500
          ),
        };

        api.start({
          scale: newScale,
          x: nextX,
          y: nextY,
        });
      },
    },
    {
      target: containerRef,
      drag: {
        threshold: 6,
      },
      pinch: { rubberband: false },
      wheel: { eventOptions: { passive: false } },
    }
  );

  // 📌 이미지 클릭 핸들러 (핀 찍기)
  const handleImageClick = (event: React.MouseEvent) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const currentScale = scale.get(); // 현재 scale 값 가져오기

    const ratio = window.innerWidth / 496;
    const isMobile = document.body.classList.contains('isMobile');
    const imgXcoord =
      x.get() - (currentScale - 1.0) * (!isMobile ? 250 : 250 * ratio);
    const imgYcoord = y.get() - (currentScale - 1.0) * 700;

    const clickXCoord = event.clientX - rect.left;
    const clickYCoord = event.clientY - rect.top;

    const pinX = (clickXCoord - imgXcoord) / currentScale;
    const pinY = (clickYCoord - imgYcoord) / currentScale;

    if (
      pinX > 0 &&
      pinY > 0 &&
      pinX < IMAGE_WIDTH &&
      pinY < IMAGE_HEIGHT &&
      spaceData
    ) {
      standardCoordData.forEach(
        (coordsData: { zone: string; coord: number[][] }) => {
          // 현재 클릭한 위치가 어디 존에 해당하는지 체크

          if (isPointInPolygon([pinX, pinY], coordsData.coord)) {
            // 해당 존에 위치한 space 찾기
            const space = spaceData.find((space) =>
              space.zone?.split(',').includes(coordsData.zone)
            );
            console.log(space, 'space');
            if (space && space.title !== '') {
              const zonesOfSpace = space.zone?.split(',');

              // 추가된 로직: zonesOfSpace에 해당하는 coord의 평균 계산
              const relevantCoords = standardCoordData
                .filter((data) => zonesOfSpace?.includes(data.zone))
                .flatMap((data) => data.coord);

              const avgX =
                relevantCoords.reduce((sum, coord) => sum + coord[0], 0) /
                relevantCoords.length;
              const avgY =
                relevantCoords.reduce((sum, coord) => sum + coord[1], 0) /
                relevantCoords.length;
              console.log('space id:', space.id, ' title:', space.title);
              setIsSpaceModalOpen(true);

              setCurrentPin({
                id: Date.now(),
                x: avgX,
                y: avgY,
                space_id: space.id,
                space_title: space.title,
                zoneState: zoneState,
                panorama_id: space.default_pano,
              });
              updateZoneState({
                space,
                setZoneState,
                setStandardCoordData,
              });
            }
          }
        }
      );
    }
  };

  // 📌 다각형 내부에 점이 있는지 확인하는 함수
  const isPointInPolygon = (point: [number, number], vs: number[][]) => {
    const [x, y] = point;
    let inside = false;
    for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
      const xi = vs[i][0],
        yi = vs[i][1];
      const xj = vs[j][0],
        yj = vs[j][1];

      const intersect =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // 📌 좌표계 변환
  const projectionMapping = (point: Panorama) => {
    return { x: point.x_img * 100 + 900, y: -point.y_img * 100 + 700 };
  };

  const handleDebuggerInfo = () => {
    return api.start({
      x: 0,
      y: 0,
      scale: 1.2,
    });
  };

  const handleOpenList = (listType: 'start' | 'end') => {
    setOpenList(listType);
  };

  const handleCloseList = () => {
    setOpenList(null);
  };

  const handleZoneState = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    optionId: string
  ) => {
    if (zoneState === optionId) return;
    e.stopPropagation();
    setZoneState(optionId);
    setCurrentPin(null);
    closeModal();
  };

  const handleStartPoint = () => {
    setValue({
      ...value,
      start: currentSpace?.title,
    });
    setStartPoint(currentPin);
    setIsSpaceModalOpen(false);
    setOpenList(null);
    if (currentPin) {
      zoomPoint(currentPin.x, currentPin.y, 1);
    }
  };

  const handleEndPoint = () => {
    setValue({
      ...value,
      end: currentSpace?.title,
    });
    setEndPoint(currentPin);
    setIsSpaceModalOpen(false);
    setOpenList(null);
    if (currentPin) {
      zoomPoint(currentPin.x, currentPin.y, 1);
    }
  };

  const handleSpaceModalClose = () => {
    setIsSpaceModalOpen(false);
    setCurrentPin(null);
  };
  const zoomPoint = (x_point: number, y_point: number, scale_point: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const targetX = centerX - x_point;
    const targetY = centerY - y_point;
    console.log(rect.left, rect.top);

    api.start({
      scale: 1,
      x: targetX,
      y: targetY,
    });
  };
  const handleFindPath = () => {
    if (!startPoint || !endPoint) return;
    const combinedObject = {
      dep: startPoint,
      arr: endPoint,
    };
    sessionStorage.setItem('xr-navi-point', JSON.stringify(combinedObject));
    sessionStorage.setItem('xr-navi-route', JSON.stringify(result));
    navigate(pagePaths.xrnavi);
  };

  const handleEscalatorPath = () => {
    setSelectedPath('Escalator');
  };

  const handleElevatorPath = () => {
    setSelectedPath('Elevator');
  };

  const handleStairPath = () => {
    setSelectedPath('Stair');
  };

  const handleShortestPath = () => {
    setSelectedPath('Short');
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const findSpaceById = (spaceId: number): Space | null => {
    return spaceData?.find((space) => space.id === spaceId) || null;
  };

  const currentSpace = currentPin ? findSpaceById(currentPin.space_id) : null;

  const buttonValidation = () => {
    if (startPoint === null || endPoint === null) {
      return false;
    }

    if (startPoint?.space_id === endPoint?.space_id) {
      return false;
    }
    if (!validated) {
      logActivateNavi(startPoint.space_title, endPoint.space_title);
      console.log('validation TRUE ::', startPoint, endPoint);

      setValidated(true);
    }

    // if (isDisabled) {
    //   return false;
    // }
    return true;
  };
  useEffect(() => {
    Globals.assign({ frameLoop: 'always' });
    logPageView('/navi');
  }, []);

  if (isLoading || !walkData) {
    return <LoadingModal isLoading={isLoading} text="데이터 로딩 중입니다" />;
  }

  return (
    <Fragment key={'navi'}>
      <Header>
        <Nav>
          <Title>
            <Link to="/main">
              <img width={24} height={24} src={arrowLeft} alt="뒤로가기" />
            </Link>
            길찾기
            {/*
            <DebugButton onClick={() => setIsDebuggerOpen(!isDebuggerOpen)} />
            <Debug
              isShow={isDebuggerOpen}
              onClick={handleDebuggerInfo}
              x={x}
              y={y}
              scale={scale}
              zoneState={zoneState}
              onClickDebug={(e: any) => {
                e.stopPropagation();
                console.log('walkData', walkData);
              }}
            />
            <DebugButton onClick={() => setIsDebuggerOpen2(!isDebuggerOpen2)} />
            <SpacePointDebug
              isShow={isDebuggerOpen2}
              startPin={startPoint}
              endPin={endPoint}
            />
             */}
          </Title>
          <FlexCenter $gap="0.5rem">
            {/* <Icon
              onClick={() => {
                openImgModal({
                  content: <ImgModal onClose={() => closeImgModal()} />,
                });
              }}
              width={24}
              height={24}
              src={scan}
              alt="스캔"
            /> */}
            <Link to="/main">
              <img width={24} height={24} src={close} alt="닫기" />
            </Link>
          </FlexCenter>
        </Nav>
        <Search
          isShowBubbleStartTooltip={false}
          isShowBubbleEndTooltip={false}
          handleOpenList={handleOpenList}
          setValue={setValue}
          value={value}
          setStartPoint={setStartPoint}
          setEndPoint={setEndPoint}
          setCurrentPin={setCurrentPin}
          handleFindPath={handleFindPath}
          buttonValidation={buttonValidation}
        />

        <SpaceFilterList
          isSearchMode={value.start !== '' && openList === 'start'}
          value={value.start}
          filterList={spaceData}
          onClick={(selectedValue) => {
            setValue((prevValue) => ({
              ...prevValue,
              start: selectedValue.title,
            }));
            setSelectedStart(selectedValue);
            updateZoneState({
              space: selectedValue,
              setZoneState,
              setStandardCoordData,
            });
            handleCloseList();
          }}
        />
        <SpaceFilterList
          isSearchMode={value.end !== '' && openList === 'end'}
          value={value.end}
          filterList={spaceData}
          onClick={(selectedValue) => {
            setValue((prevValue) => ({
              ...prevValue,
              end: selectedValue.title,
            }));
            setSelectedEnd(selectedValue);
            updateZoneState({
              space: selectedValue,
              setZoneState,
              setStandardCoordData,
            });
            handleCloseList();
          }}
        />
      </Header>

      <MapContainer ref={containerRef} onClick={handleImageClick}>
        <InfoWrapper>
          <ToolTipList />
          <Chip matchedFloor={matchedFloor} />
        </InfoWrapper>
        <animated.div
          id={'image-wrapper-navi'}
          className="image-wrapper-navi"
          style={{
            x,
            y,
            scale,
            touchAction: 'none',
            userSelect: 'none',
            position: 'relative',
          }}
        >
          <img
            src={getZoneMap(zoneState)}
            alt="Interactive"
            className="image"
            draggable="false"
            style={{
              position: 'relative',
            }}
          />
          {currentPin && (
            <CurrentPinMarker
              src={map_current_pin}
              draggable={false}
              style={{
                position: 'absolute',
                left: `${currentPin.x}px`,
                top: `${currentPin.y}px`,
              }}
            />
          )}

          {startPoint && startPoint.zoneState === zoneState && (
            <CurrentPinMarker
              src={map_start_pin}
              draggable={false}
              style={{
                position: 'absolute',
                left: `${startPoint.x}px`,
                top: `${startPoint.y}px`,
              }}
            />
          )}
          {endPoint && endPoint.zoneState === zoneState && (
            <CurrentPinMarker
              src={map_end_pin}
              draggable={false}
              style={{
                position: 'absolute',
                left: `${endPoint.x}px`,
                top: `${endPoint.y}px`,
              }}
            />
          )}
          {/* FIXME : NEED TO DELETE 데이터 확인 용 */}
          {/* {walkData
            .find(
              (walk) =>
                mapOptions.find((item) => walk.id === item.walkway_id)?.id ===
                zoneState
            )
            ?.pano.map((walk) => {
              const { x, y } = projectionMapping(walk);
              return (
                <WalkwayMarker
                  key={walk.id}
                  style={{
                    position: 'absolute',
                    left: `${x}px`,
                    top: `${y}px`,
                  }}
                  onClick={() => {
                    console.log(x, y);
                  }}
                />
              );
            })} */}
          {result && (
            <>
              {/* SVG 내부에서는 선만 렌더링 */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  left: 0,
                  top: 0,
                  position: 'absolute',
                  width: '1800px',
                  height: '1400px',
                }}
              >
                {result.panorama_path.map((data: any, index: number) => {
                  if (index === result.panorama_path.length - 1) return null;

                  const from = walkData
                    .find(
                      (walk) =>
                        mapOptions.find((item) => walk.id === item.walkway_id)
                          ?.id === zoneState
                    )
                    ?.pano.find((p) => p.id === data.id);
                  const to = walkData
                    .find(
                      (walk) =>
                        mapOptions.find((item) => walk.id === item.walkway_id)
                          ?.id === zoneState
                    )
                    ?.pano.find(
                      (p) => p.id === result.panorama_path[index + 1].id
                    );

                  if (from && to) {
                    const { x: x_from, y: y_from } = projectionMapping(from);
                    const { x: x_to, y: y_to } = projectionMapping(to);
                    return (
                      <line
                        key={index}
                        x1={x_from}
                        y1={y_from}
                        x2={x_to}
                        y2={y_to}
                        stroke={'red'}
                        strokeWidth={5}
                      />
                    );
                  }
                  return null;
                })}
              </svg>

              {/* 마커를 SVG 위에 렌더링 */}
              {result.panorama_path.map((data: any, index: number) => {
                if (index === result.panorama_path.length - 1) return null;

                const from = walkData
                  .find(
                    (walk) =>
                      mapOptions.find((item) => walk.id === item.walkway_id)
                        ?.id === zoneState
                  )
                  ?.pano.find((p) => p.id === data.id);
                const to = walkData
                  .find(
                    (walk) =>
                      mapOptions.find((item) => walk.id === item.walkway_id)
                        ?.id === zoneState
                  )
                  ?.pano.find(
                    (p) => p.id === result.panorama_path[index + 1].id
                  );

                if (from && !to) {
                  const buttonType =
                    data.category !== 'elevator' &&
                    data.category !== 'escalator'
                      ? 'exit'
                      : data.category ===
                        result.panorama_path[index + 1]!.category
                      ? data.category
                      : 'exit';
                  const { x: x_from, y: y_from } = projectionMapping(from);
                  return (
                    <TransitPin
                      key={`from-${index}`}
                      type={buttonType}
                      left={`${x_from}px`}
                      top={`${y_from}px`}
                      translateX="-50%"
                      translateY="-50%"
                    />
                  );
                } else if (!from && to) {
                  const buttonType =
                    data.category !== 'elevator' &&
                    data.category !== 'escalator'
                      ? 'exit'
                      : data.category ===
                        result.panorama_path[index + 1]!.category
                      ? data.category
                      : 'exit';
                  const { x: x_to, y: y_to } = projectionMapping(to);
                  return (
                    <TransitPin
                      key={`to-${index}`}
                      type={buttonType}
                      left={`${x_to}px`}
                      top={`${y_to}px`}
                      translateX="-50%"
                      translateY="-50%"
                    />
                  );
                } else {
                  return null;
                }
              })}
            </>
          )}
        </animated.div>
      </MapContainer>

      <ButtonSheetButton onClick={openModal}>
        {mapOptions.find((item) => item.id === zoneState)?.floor}
        <img src={caret} alt="caret" />
      </ButtonSheetButton>
      <BottomSheet setIsOpen={setIsModalOpen} isOpen={isModalOpen}>
        <BottomSheetTitle>맵 선택</BottomSheetTitle>
        <Grid>
          {mapOptions.map((option) => (
            <GridItem
              $isSelect={zoneState === option.id}
              onClick={(e) => handleZoneState(e, option.id)}
              $isEmpty={false}
              key={option.id}
            >
              {option.floor}
            </GridItem>
          ))}
          {Array.from({ length: 2 }).map((_, index) => (
            <GridItem $isEmpty={true} key={`empty-${index}`}></GridItem>
          ))}
        </Grid>
      </BottomSheet>

      <FloatingButton
        onRouteClick={handleShortestPath}
        onStairClick={handleStairPath}
        onEscalatorClick={handleEscalatorPath}
        onElevatorClick={handleElevatorPath}
        selectedPath={selectedPath}
      />
      <SpaceModal
        isOpen={isSpaceModalOpen}
        setClose={handleSpaceModalClose}
        handleSelectStart={handleStartPoint}
        handleSelectEnd={handleEndPoint}
        space={currentSpace as Space}
      />
    </Fragment>
  );
};

const FlexCenter = styled.div<{ $gap: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.$gap};
`;

const ButtonSheetButton = styled.button`
  ${body2};
  font-weight: 700;
  color: ${palette.primaryBlack950};
  position: absolute;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 62.5rem;
  background: white;
  width: 13.125rem;
  padding: 0.625rem 0.75rem 0.625rem 1.25rem;
  border: 1px solid ${palette.gray50};
  box-shadow: 0px 0px 1px 0px rgba(0, 0, 0, 0.16),
    0px 4px 8px 0px rgba(0, 0, 0, 0.04), 0px 8px 48px 0px rgba(0, 0, 0, 0.02);
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 22;
`;

const Header = styled.header`
  background: ${palette.gray50};
`;

const Title = styled.h1`
  display: flex;
  gap: 0.75rem;
  color: ${palette.primaryBlack950};
  ${h3};
`;
const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;
  border: 0.5px solid ${palette.gray100};
  padding: 1rem 1.25rem;
  background: ${palette.white};
`;

const InfoWrapper = styled.div`
  position: absolute;

  display: flex;
  gap: 0.75rem;
  align-items: center;
  top: 1rem;
  left: 0.75rem;
  z-index: 22;
`;

const CurrentPinMarker = styled.img`
  width: 280px;
  height: 280px;
  transform: translate(-50%, -55%);
`;

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  z-index: 19;
  overflow: hidden;
  position: relative;
`;

const BottomSheetTitle = styled.h2`
  ${body1};
  font-weight: 700;
  padding: 0 1.5rem;
  margin-bottom: 1rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 0;
  width: 100%;
  border-top: 1px solid ${palette.gray50};
  border-right: 1px solid ${palette.gray50};
`;

const GridItem = styled.div<{ $isSelect?: boolean; $isEmpty: boolean }>`
  ${body2};
  font-weight: 700;
  color: ${(props) =>
    props.$isSelect ? palette.white : palette.primaryBlack950};
  display: flex;
  justify-content: center;
  align-items: center;
  height: 3.75rem;

  background: ${(props) =>
    props.$isSelect ? palette.secondaryRed700 : palette.white};
  transition: background 0.3s ease-in-out;
  padding: 0.625rem 0.75rem 0.625rem 1rem;

  border-left: 1px solid ${palette.gray50};
  border-bottom: 1px solid ${palette.gray50};
  box-shadow: 0px 0px 1px 0px rgba(0, 0, 0, 0.16),
    0px 4px 8px 0px rgba(0, 0, 0, 0.04), 0px 8px 48px 0px rgba(0, 0, 0, 0.02);
`;

const WalkwayMarker = styled.div`
  width: 10px;
  height: 10px;
  background: red;
  border-radius: 50%;
  position: absolute;
  transform: translate(-50%, -50%);
`;

export default NaviContainer;
